// Add lazy (@mux/mux-player-react/lazy) in the future and fix the hover effect with lazy
import MuxPlayer from "@mux/mux-player-react";
import { h } from "preact";
import { useRef, useEffect } from "preact/hooks";

// Boolean types must be strings like "true" or "false" to be accepted by mux

export default function MuxReact(props) {
  const videoRef = useRef(null);
  const wasPlaying = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const parent = video.parentElement;
    if (!parent.classList.contains("data-play-hover")) return;

    const handleMouseEnter = () => {
      wasPlaying.current = !video.paused;
      if (video.paused) {
        video.play().catch(() => {});
      }
    };

    const handleMouseLeave = () => {
      if (!wasPlaying.current && !video.paused) {
        video.pause();
      }
    };

    video.addEventListener("mouseenter", handleMouseEnter);
    video.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      video.removeEventListener("mouseenter", handleMouseEnter);
      video.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    const videoContainer = video.parentElement;
    const containerParent = videoContainer.parentElement;
    // Need to go up one more level to the <a> tag that contains both the progress bar and video
    const linkContainer = containerParent?.parentElement;
    const progressBar = linkContainer
      ? linkContainer.querySelector(".video-progress-bar")
      : videoContainer.nextElementSibling?.classList.contains(
          "video-progress-bar"
        )
      ? videoContainer.nextElementSibling
      : null;

    if (!progressBar) {
      console.warn(
        'No progress bar element found for video. Add a div with class "video-progress-bar" to the video component markup.'
      );
      return;
    }

    let animationFrame = null;
    let lastUpdateTime = 0;

    const updateProgress = () => {
      const currentTime = performance.now();

      // Throttle updates to ~60fps for smoother animation
      if (currentTime - lastUpdateTime > 16) {
        if (video.duration && video.currentTime >= 0) {
          const progress = (video.currentTime / video.duration) * 100;
          progressBar.style.width = `${Math.min(progress, 100)}%`;
        }
        lastUpdateTime = currentTime;
      }

      // Continue animation while playing
      if (!video.paused && !video.ended) {
        animationFrame = requestAnimationFrame(updateProgress);
      }
    };

    const handlePlay = () => {
      progressBar.classList.remove("hidden");
      // Add smooth transition for width changes
      progressBar.style.transition = "width 0.1s linear";

      // Cancel any existing animation frame
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      // Start smooth updates
      animationFrame = requestAnimationFrame(updateProgress);
    };

    const handlePause = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
    };

    const handleEnd = () => {
      progressBar.classList.add("hidden");
      progressBar.style.width = "100%";
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
    };

    const handleSeeking = () => {
      // Disable transition for immediate seek updates
      progressBar.style.transition = "none";
      if (video.duration && video.currentTime >= 0) {
        const progress = (video.currentTime / video.duration) * 100;
        progressBar.style.width = `${Math.min(progress, 100)}%`;
      }
    };

    const handleSeeked = () => {
      // Re-enable smooth transition
      progressBar.style.transition = "width 0.1s linear";
    };

    const handleBeforeUnload = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnd);
    video.addEventListener("seeking", handleSeeking);
    video.addEventListener("seeked", handleSeeked);

    progressBar.style.width = "0%";
    progressBar.style.transition = "width 0.1s linear";

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnd);
      video.removeEventListener("seeking", handleSeeking);
      video.removeEventListener("seeked", handleSeeked);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  });

  return h(MuxPlayer, {
    ...props,
    muted: "true",
    loop: "true",
    crossorigin: "true",
    playsinline: "true",
    maxResolution: "1080p",
    //preload: "metadata",
    ref: videoRef,
    style: {
      "--controls": "none",
      "--media-object-fit": "cover",
      inset: 0,
    },
  });
}
